import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { ProgressCard } from 'design-system/src/components/ProgressCard/ProgressCard';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { CardContent, StyledSignupForm, NameRow, DesktopContainer, CardSubSection } from './styles';
import { EMAIL_VALIDATION_REGEX } from '../../constants';
import { ReturnToHomePageButton } from './ReturnToHomePageButton';
import { PageContainerWithLogo } from './PageContainerWithLogo.tsx';
import { Input } from '../Input';
import { ControllerWrapper } from './ControllerWrapper.tsx';
import { getRequiredErrorMessage } from './utils.tsx';
import { client } from '../../api';
import toast from 'react-hot-toast';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import * as Sentry from '@sentry/react';
import styled from 'styled-components';
import { SuccessCard } from 'design-system/src/components/SuccessCard/SuccessCard';

const NameField = styled.div`
  flex: 1;
`;

type JoinRequestFormData = {
  first_name: string;
  last_name: string;
  email: string;
  practice_name: string;
  office_address: string;
};

export type JoinExistingPracticeProps = {
  className?: string;
};

export const JoinExistingPractice = ({ className }: JoinExistingPracticeProps) => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<JoinRequestFormData>();

  const submitJoinRequest = useMutation({
    mutationFn: async (formData: JoinRequestFormData) => {
      const response = await client.post('/signup/submit-practice-join-request', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        practice_name: formData.practice_name,
        office_address: formData.office_address,
      });
      return response.data;
    },
    onSuccess: () => {
      setIsSubmitted(true);
    },
    onError: (error) => {
      Sentry.captureException(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const handleFormSubmit = async (formData: JoinRequestFormData) => {
    submitJoinRequest.mutate(formData);
  };

  if (isSubmitted) {
    return (
      <PageContainerWithLogo className={className}>
        <SuccessCard>
          <CardContent>
            <CardSubSection>
              <Typography styledAs="h6">Request submitted</Typography>
              <Typography>We have received your request to join your practice on Tandem.</Typography>
              <Typography>
                In the meantime, reach out to your organization admins who can add you to the Tandem portal from the{' '}
                <strong>Users</strong> page. Admins include the user who created your practice on Tandem.
              </Typography>
            </CardSubSection>
            <ReturnToHomePageButton />
          </CardContent>
        </SuccessCard>
      </PageContainerWithLogo>
    );
  }

  return (
    <PageContainerWithLogo className={className}>
      <ProgressCard progressBar={{ current: 1, total: 4 }}>
        <CardContent>
          <CardSubSection>
            <Typography styledAs="h5">Request to join your practice</Typography>
            <DesktopContainer>
              <Typography>
                Use this form to request access to a practice, or ask your practice admin to send you an invite directly
                from their portal.
              </Typography>
            </DesktopContainer>
          </CardSubSection>
          <StyledSignupForm onSubmit={handleSubmit(handleFormSubmit)}>
            <NameRow>
              <NameField>
                <ControllerWrapper>
                  <Controller
                    control={control}
                    name="first_name"
                    rules={{ required: { value: true, message: getRequiredErrorMessage('First name') } }}
                    render={({ field }) => (
                      <Input label="First Name" hasError={!!errors.first_name} placeholder="John" {...field} />
                    )}
                  />
                  {errors.first_name && <ErrorMessage>{errors.first_name.message}</ErrorMessage>}
                </ControllerWrapper>
              </NameField>

              <NameField>
                <ControllerWrapper>
                  <Controller
                    control={control}
                    name="last_name"
                    rules={{ required: { value: true, message: getRequiredErrorMessage('Last name') } }}
                    render={({ field }) => (
                      <Input label="Last Name" hasError={!!errors.last_name} placeholder="Doe" {...field} />
                    )}
                  />
                  {errors.last_name && <ErrorMessage>{errors.last_name.message}</ErrorMessage>}
                </ControllerWrapper>
              </NameField>
            </NameRow>

            <ControllerWrapper>
              <Controller
                control={control}
                name="email"
                rules={{
                  required: { value: true, message: getRequiredErrorMessage('Email') },
                  pattern: {
                    value: EMAIL_VALIDATION_REGEX,
                    message: 'Invalid email format',
                  },
                }}
                render={({ field }) => (
                  <Input
                    label="Your Work Email"
                    hasError={!!errors.email}
                    placeholder="john.doe@example.com"
                    {...field}
                  />
                )}
              />
              {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
            </ControllerWrapper>

            <ControllerWrapper>
              <Controller
                control={control}
                name="practice_name"
                rules={{ required: { value: true, message: getRequiredErrorMessage('Practice name') } }}
                render={({ field }) => (
                  <Input
                    label="Practice Name"
                    hasError={!!errors.practice_name}
                    placeholder="e.g. New York Specialty Partners"
                    {...field}
                  />
                )}
              />
              {errors.practice_name && <ErrorMessage>{errors.practice_name.message}</ErrorMessage>}
            </ControllerWrapper>

            <ControllerWrapper>
              <Controller
                control={control}
                name="office_address"
                rules={{ required: { value: true, message: getRequiredErrorMessage('Practice address') } }}
                render={({ field }) => (
                  <Input
                    label="Practice Address"
                    type="text"
                    hasError={!!errors.office_address}
                    placeholder="E.g. 123 Main St, Philadelphia, PA 191099"
                    {...field}
                  />
                )}
              />
              {errors.office_address && (
                <ErrorMessage marginBottom="16px">{errors.office_address.message}</ErrorMessage>
              )}
            </ControllerWrapper>

            <Button size="lg" width="full" type="submit" isLoading={submitJoinRequest.isPending}>
              Next
            </Button>
          </StyledSignupForm>
        </CardContent>
      </ProgressCard>
    </PageContainerWithLogo>
  );
};

export default JoinExistingPractice;
